/**
 * Where the rider is.
 *
 * Replaces MapLibre's built-in GeolocateControl, which went away with MapLibre. Google's
 * equivalent does not exist — its default UI has no locate button — so this is a small
 * hook plus a button in the map chrome.
 *
 * Watching rather than polling: the position matters continuously while riding, and
 * `watchPosition` lets the platform coalesce updates instead of us waking the GPS on a
 * timer and flattening the battery over a nine-hour shift.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Position {
  lat: number;
  lng: number;
  /** Metres. Used to decide whether the fix is worth acting on. */
  accuracy: number;
}

export type GeolocationStatus = 'idle' | 'locating' | 'tracking' | 'denied' | 'unavailable';

export interface GeolocationState {
  position: Position | null;
  status: GeolocationStatus;
  /** Start watching. Called from a user gesture, which is what iOS requires. */
  start: () => void;
  stop: () => void;
}

/**
 * A fix worse than this is not worth showing — at 500 m of error the marker would imply
 * a precision the map does not have, on a grid whose cells are about 170 m across.
 */
const MAX_USEFUL_ACCURACY_M = 500;

export function useGeolocation(): GeolocationState {
  const [position, setPosition] = useState<Position | null>(null);
  const [status, setStatus] = useState<GeolocationStatus>('idle');
  const watchId = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setStatus('idle');
  }, []);

  const start = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    if (watchId.current !== null) return;

    setStatus('locating');

    watchId.current = navigator.geolocation.watchPosition(
      (fix) => {
        if (fix.coords.accuracy > MAX_USEFUL_ACCURACY_M) return;
        setPosition({
          lat: fix.coords.latitude,
          lng: fix.coords.longitude,
          accuracy: fix.coords.accuracy,
        });
        setStatus('tracking');
      },
      (error) => {
        setStatus(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
        watchId.current = null;
      },
      {
        enableHighAccuracy: true,
        // A fix up to 15s old is fine — at ebike speed that is a couple of hundred
        // metres, and it avoids forcing a fresh GPS lock on every update.
        maximumAge: 15_000,
        timeout: 20_000,
      },
    );
  }, []);

  // Release the watch when the map goes away, so a backgrounded tab is not holding the
  // GPS open.
  useEffect(() => stop, [stop]);

  return { position, status, start, stop };
}
